/**
 * Parsing a chapter, in full or incrementally after edits (decision 36: parsed once, incrementally). An
 * incremental parse reuses the unchanged parts of the previous tree and always equals a full parse of the
 * new text.
 */
import { TreeFragment } from '@lezer/common';
import type { ChangedRange, Tree } from '@lezer/common';
import { chapterParser } from './grammar.ts';

/** A chapter's source and its syntax tree. */
export interface ParsedChapter {
  readonly text: string;
  readonly tree: Tree;
}

/**
 * One edit: replace `from`–`to` of the previous text with `insert`. The changes passed together are in
 * the previous text's positions, sorted, and don't overlap (two insertions at one point apply in order),
 * like a CodeMirror change set.
 */
export interface TextChange {
  readonly from: number;
  readonly to: number;
  readonly insert: string;
}

/** Thrown for a change that is out of range, reversed, unsorted or overlapping. */
export class TextChangeError extends RangeError {
  override readonly name = 'TextChangeError';
}

/** Parses a whole chapter. */
export function parseChapter(text: string): ParsedChapter {
  return { text, tree: chapterParser.parse(text) };
}

/** The new text after `changes`, and the changed ranges in both texts' positions. Pure. */
export function applyChanges(text: string, changes: readonly TextChange[]): { text: string; ranges: ChangedRange[] } {
  const pieces: string[] = [];
  const ranges: ChangedRange[] = [];
  let copied = 0;
  let shift = 0;
  for (const { from, to, insert } of changes) {
    if (!Number.isInteger(from) || !Number.isInteger(to) || from < copied || to < from || to > text.length) {
      throw new TextChangeError(`change ${from}–${to} is not a sorted, in-range, non-overlapping range of ${text.length} characters`);
    }
    pieces.push(text.slice(copied, from), insert);
    ranges.push({ fromA: from, toA: to, fromB: from + shift, toB: from + shift + insert.length });
    shift += insert.length - (to - from);
    copied = to;
  }
  pieces.push(text.slice(copied));
  return { text: pieces.join(''), ranges };
}

/** Parses the chapter after `changes`, reusing what the edits left untouched in `previous.tree`. */
export function reparseChapter(previous: ParsedChapter, changes: readonly TextChange[]): ParsedChapter {
  const { text, ranges } = applyChanges(previous.text, changes);
  const fragments = TreeFragment.applyChanges(TreeFragment.addTree(previous.tree), ranges);
  return { text, tree: chapterParser.parse(text, fragments) };
}
